import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

interface Favorite {
  id: string;
  menu_item_url: string;
  sequence: number;
}

export const useFavorites = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: favorites = [], isLoading } = useQuery({
    queryKey: ['favorites', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('user_favorites')
        .select('*')
        .eq('user_id', user.id)
        .order('sequence', { ascending: true });

      if (error) throw error;
      return data as Favorite[];
    },
    enabled: !!user,
  });

  const addFavorite = useMutation({
    mutationFn: async ({ url, targetIndex }: { url: string; targetIndex: number }) => {
      if (!user) throw new Error('Not authenticated');

      const updatedFavorites = favorites.map((fav, index) => ({
        ...fav,
        sequence: index >= targetIndex ? index + 1 : index,
      }));

      for (const fav of updatedFavorites) {
        await supabase
          .from('user_favorites')
          .update({ sequence: fav.sequence })
          .eq('id', fav.id);
      }

      const { error } = await supabase
        .from('user_favorites')
        .insert({
          user_id: user.id,
          menu_item_url: url,
          sequence: targetIndex,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
      toast.success('Added to favorites');
    },
    onError: () => {
      toast.error('Failed to add to favorites');
    },
  });

  const removeFavorite = useMutation({
    mutationFn: async (url: string) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('menu_item_url', url);

      if (error) throw error;

      const remaining = favorites.filter((f) => f.menu_item_url !== url);
      for (let i = 0; i < remaining.length; i++) {
        await supabase
          .from('user_favorites')
          .update({ sequence: i })
          .eq('id', remaining[i].id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
      toast.success('Removed from favorites');
    },
    onError: () => {
      toast.error('Failed to remove from favorites');
    },
  });

  const reorderFavorites = useMutation({
    mutationFn: async (reorderedUrls: string[]) => {
      if (!user) throw new Error('Not authenticated');

      for (let i = 0; i < reorderedUrls.length; i++) {
        const favorite = favorites.find((f) => f.menu_item_url === reorderedUrls[i]);
        if (favorite) {
          await supabase
            .from('user_favorites')
            .update({ sequence: i })
            .eq('id', favorite.id);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  return {
    favorites,
    isLoading,
    addFavorite: addFavorite.mutate,
    removeFavorite: removeFavorite.mutate,
    reorderFavorites: reorderFavorites.mutate,
  };
};
